import { isAxiosError } from "axios";
import { z } from "zod";
import {
	type CvpRangeMachine,
	type CvpRangeMachineRequest,
	cvpRangeMachineSchema,
	prepareCvpRangeMachineData,
	type MachineCorrugateTrack,
} from "@packsize/schemas/machines";

import { packNetAxios } from "@/lib/api-client";

import { baseRoutes } from "../base-routes";

export async function createCvpRangeMachine(data: CvpRangeMachineRequest): Promise<void> {
	await packNetAxios.post(`${baseRoutes.cvpRange}/api/v1/machines`, data);
}

export async function updateCvpRangeMachine(
	tenantId: string,
	machineId: string,
	data: CvpRangeMachineRequest,
): Promise<void> {
	// TODO @eb: Remove machineId and tenantId from the payload when the backend no longer requires it.
	await packNetAxios.put(`${baseRoutes.cvpRange}/api/v1/machines/${machineId}`, {
		id: machineId,
		tenantId,
		...data,
	});
}

export async function getCvpRangeMachines(): Promise<CvpRangeMachine[]> {
	try {
		const response = await packNetAxios.get(`${baseRoutes.cvpRange}/api/v1/machines`);

		if (Array.isArray(response.data) === false) {
			throw new Error("Unable to fetch CVP Range machines, response is not a list");
		}

		return z
			.array(cvpRangeMachineSchema)
			.enrich({ callSite: "getCvpRangeMachines - v2" })
			.parseWithTelemetry(response.data.map(prepareCvpRangeMachineData));
	} catch (error) {
		if (isAxiosError(error) && error.response?.status === 404) {
			return [];
		}

		throw error;
	}
}

export async function getCvpRangeMachine(machineId: string): Promise<CvpRangeMachine | null> {
	const response = await packNetAxios.get(`${baseRoutes.cvpRange}/api/v1/machines/${machineId}`);

	if (response.status !== 200) {
		return null;
	}

	return cvpRangeMachineSchema
		.enrich({ callSite: "getCvpRangeMachine - v2" })
		.parseWithTelemetry(prepareCvpRangeMachineData(response.data));
}

export async function putTrackConfigurations(
	machineId: string,
	data: MachineCorrugateTrack[],
	abortController?: AbortController,
): Promise<CvpRangeMachine> {
	const response = await packNetAxios.put<CvpRangeMachine>(
		`${baseRoutes.cvpRange}/api/v1/machines/${machineId}/tracks`,
		data,
		{
			signal: abortController?.signal,
		},
	);
	return response.data;
}
